import { useEffect, useState } from 'react'
import { fetchBackupSettings, updateBackupSettings } from '@/api/backup'
import { Button } from '@/components/ui/button'
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'

const textareaClassName =
  'flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'

function hostnamesToText(hostnames) {
  return (hostnames ?? []).join('\n')
}

function textToHostnames(text) {
  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
}

function expectedVmsToText(expectedVms) {
  const entries = Object.entries(expectedVms ?? {})
  return entries
    .map(([host, vms]) => `${host}: ${(vms ?? []).join(', ')}`)
    .join('\n')
}

function textToExpectedVms(text) {
  const result = {}
  for (const rawLine of text.split(/\r?\n/)) {
    const line = rawLine.trim()
    if (!line) continue
    const separator = line.indexOf(':')
    if (separator <= 0) continue
    const host = line.slice(0, separator).trim()
    if (!host) continue
    const vms = line
      .slice(separator + 1)
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean)
    if (vms.length === 0) continue
    result[host] = vms
  }
  return result
}

function expectedVmsEqual(left, right) {
  return expectedVmsToText(left) === expectedVmsToText(right)
}

function LegacySplitNotice() {
  return (
    <div className="rounded-md border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-sm text-amber-900 dark:text-amber-200">
      Hostnames were previously shared in one list. Move Synology NAS names into{' '}
      <span className="font-medium">NAS Backup</span>, remove them from{' '}
      <span className="font-medium">Server Backup</span>, then save both sections.
    </div>
  )
}

export function ServerBackupSettingsCard({ embedded = false }) {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [nasListConfigured, setNasListConfigured] = useState(true)
  const [savedHostnames, setSavedHostnames] = useState([])
  const [savedExpectedVms, setSavedExpectedVms] = useState({})
  const [hostnamesText, setHostnamesText] = useState('')
  const [expectedVmsText, setExpectedVmsText] = useState('')

  useEffect(() => {
    fetchBackupSettings()
      .then((data) => {
        setSavedHostnames(data.server_hostnames ?? [])
        setHostnamesText(hostnamesToText(data.server_hostnames))
        setSavedExpectedVms(data.expected_vms ?? {})
        setExpectedVmsText(expectedVmsToText(data.expected_vms))
        setNasListConfigured(Boolean(data.nas_list_configured))
      })
      .catch(() => setError('Could not load Server Backup settings.'))
      .finally(() => setLoading(false))
  }, [])

  const draftHostnames = textToHostnames(hostnamesText)
  const draftExpectedVms = textToExpectedVms(expectedVmsText)
  const hasUnsavedChanges =
    hostnamesToText(draftHostnames) !== hostnamesToText(savedHostnames) ||
    !expectedVmsEqual(draftExpectedVms, savedExpectedVms)

  async function handleSave(event) {
    event.preventDefault()
    setSaving(true)
    setError('')
    setMessage('')
    try {
      const updated = await updateBackupSettings({
        server_hostnames: draftHostnames,
        expected_vms: draftExpectedVms,
      })
      setSavedHostnames(updated.server_hostnames ?? [])
      setHostnamesText(hostnamesToText(updated.server_hostnames))
      setSavedExpectedVms(updated.expected_vms ?? {})
      setExpectedVmsText(expectedVmsToText(updated.expected_vms))
      setNasListConfigured(Boolean(updated.nas_list_configured))
      setMessage('Server Backup settings saved.')
    } catch {
      setError('Could not save Server Backup settings.')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <Skeleton className="h-48 w-full" />
  }

  const expectedHostCount = Object.keys(savedExpectedVms).length
  const expectedVmCount = Object.values(savedExpectedVms).reduce(
    (total, vms) => total + (vms?.length ?? 0),
    0,
  )

  return (
    <div className="space-y-4">
      {!embedded ? (
        <Card>
          <CardHeader>
            <CardTitle>Server Backup</CardTitle>
            <CardDescription>
              Altaro backup server hostnames and expected guest VMs for the Server Backup page.
            </CardDescription>
          </CardHeader>
        </Card>
      ) : null}

      <form onSubmit={handleSave} className="space-y-4">
        {embedded ? (
          <p className="text-sm text-muted-foreground">
            Altaro Windows backup server hostnames for the Server Backup page. Synology NAS
            devices belong under NAS Backup.
          </p>
        ) : null}

        {!nasListConfigured ? <LegacySplitNotice /> : null}

        <div className="space-y-2">
          <Label htmlFor="server-backup-hostnames">Altaro backup server hostnames</Label>
          <textarea
            id="server-backup-hostnames"
            rows={8}
            value={hostnamesText}
            onChange={(event) => setHostnamesText(event.target.value)}
            placeholder={'BRBHV01\nBRBHV02\nCASEMANHV01'}
            className={textareaClassName}
          />
          <p className="text-xs text-muted-foreground">
            One hostname per line. Matching is case-insensitive and uses the computer name sent by
            the Altaro ingest script.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="backup-expected-vms">Expected guest VMs</Label>
          <textarea
            id="backup-expected-vms"
            rows={8}
            value={expectedVmsText}
            onChange={(event) => setExpectedVmsText(event.target.value)}
            placeholder={'BRBHV01: BRBExchange, BRBWIN10VM\nBRBHV02: BRBD, BRBFAP'}
            className={textareaClassName}
          />
          <p className="text-xs text-muted-foreground">
            One backup host per line: <span className="font-mono">HOSTNAME: VM1, VM2</span>.
            Use the guest VM names as they appear in Altaro. The Server Backup page flags any
            expected guest that is missing from the latest report. Hosts must also be listed above;
            matching is case-insensitive.
          </p>
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}
        {message && <p className="text-sm text-emerald-600">{message}</p>}

        <div className="flex items-center gap-3">
          <Button type="submit" disabled={saving || !hasUnsavedChanges}>
            {saving ? 'Saving…' : 'Save Server Backup'}
          </Button>
          <p className="text-xs text-muted-foreground">
            {savedHostnames.length} server{savedHostnames.length === 1 ? '' : 's'} configured
            {expectedVmCount > 0
              ? ` · ${expectedVmCount} expected guest${expectedVmCount === 1 ? '' : 's'} on ${expectedHostCount} host${expectedHostCount === 1 ? '' : 's'}`
              : null}
          </p>
        </div>
      </form>
    </div>
  )
}

export function NasBackupSettingsCard({ embedded = false }) {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [nasListConfigured, setNasListConfigured] = useState(true)
  const [savedHostnames, setSavedHostnames] = useState([])
  const [hostnamesText, setHostnamesText] = useState('')

  useEffect(() => {
    fetchBackupSettings()
      .then((data) => {
        const configured = Boolean(data.nas_list_configured)
        setNasListConfigured(configured)
        // Until the lists are split, leave this box empty so NAS names can be moved here.
        const hostnames = configured ? (data.nas_hostnames ?? []) : []
        setSavedHostnames(hostnames)
        setHostnamesText(hostnamesToText(hostnames))
      })
      .catch(() => setError('Could not load NAS Backup settings.'))
      .finally(() => setLoading(false))
  }, [])

  const draftHostnames = textToHostnames(hostnamesText)
  const hasUnsavedChanges =
    hostnamesToText(draftHostnames) !== hostnamesToText(savedHostnames)

  async function handleSave(event) {
    event.preventDefault()
    setSaving(true)
    setError('')
    setMessage('')
    try {
      const updated = await updateBackupSettings({
        nas_hostnames: draftHostnames,
      })
      setSavedHostnames(updated.nas_hostnames ?? [])
      setHostnamesText(hostnamesToText(updated.nas_hostnames))
      setNasListConfigured(Boolean(updated.nas_list_configured))
      setMessage('NAS Backup settings saved.')
    } catch {
      setError('Could not save NAS Backup settings.')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <Skeleton className="h-32 w-full" />
  }

  return (
    <div className="space-y-4">
      {!embedded ? (
        <Card>
          <CardHeader>
            <CardTitle>NAS Backup</CardTitle>
            <CardDescription>
              Synology NAS hostnames for the NAS Backup page (Hyper Backup, Cloud Sync, and NAS
              health).
            </CardDescription>
          </CardHeader>
        </Card>
      ) : null}

      <form onSubmit={handleSave} className="space-y-4">
        {embedded ? (
          <p className="text-sm text-muted-foreground">
            Synology NAS hostnames for the NAS Backup page. Hyper Backup and Cloud Sync both report
            against the same hostname.
          </p>
        ) : null}

        {!nasListConfigured ? <LegacySplitNotice /> : null}

        <div className="space-y-2">
          <Label htmlFor="nas-backup-hostnames">Synology NAS hostnames</Label>
          <textarea
            id="nas-backup-hostnames"
            rows={8}
            value={hostnamesText}
            onChange={(event) => setHostnamesText(event.target.value)}
            placeholder={'BekatNAS\nCasemanNAS'}
            className={textareaClassName}
          />
          <p className="text-xs text-muted-foreground">
            One hostname per line. Matching is case-insensitive and uses the computer name sent by
            the NAS ingest scripts.
          </p>
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}
        {message && <p className="text-sm text-emerald-600">{message}</p>}

        <div className="flex items-center gap-3">
          <Button type="submit" disabled={saving || !hasUnsavedChanges}>
            {saving ? 'Saving…' : 'Save NAS Backup'}
          </Button>
          <p className="text-xs text-muted-foreground">
            {nasListConfigured
              ? `${savedHostnames.length} NAS host${savedHostnames.length === 1 ? '' : 's'} configured`
              : 'Not split yet — NAS page still uses the shared host list'}
          </p>
        </div>
      </form>
    </div>
  )
}

/** @deprecated Prefer ServerBackupSettingsCard */
export function BackupSettingsCard(props) {
  return <ServerBackupSettingsCard {...props} />
}
